import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Icon, Button, Loader, Dimmer } from 'semantic-ui-react';
import SusuModal from '../../common/SusuModal';
import './FitHistorySection.css';
import { selectSelectedVTOResultId, selectVTOResult as selectVTOResultAction, updateVTOSession } from '../../../redux/slices/vtoSlice';
import { setActiveItems } from '../../../redux/slices/clothingItemsSlice';
import { setActivePersonImage } from '../../../redux/slices/personImageSlice';
import { useVTOResults } from '../../../hooks/useVTOResults';
import { usePersonImage } from '../../../hooks/usePersonImage';
import { getSmallThumbnailUrl } from '../../../utils/imageUrl';
import usePersonImages from '../../../hooks/usePersonImages';
import { useDeleteVTOResultMutation } from '../../../redux/apis/endpoints/vtoResultApi';
import { useGetUserClothingQuery } from '../../../redux/apis/endpoints/closetApi';
import { useUpdatePersonImageMutation as useUpdatePersonImageMutationFromPersonImageApi } from '../../../redux/apis/endpoints/personImageApi';

const FitHistorySection = ({ onOpenLookbookPanel }) => {
  const dispatch = useDispatch();
  const personImageState = usePersonImage();

  // Handle click on LOOKS > to open lookbook panel
  const handleGoToLookbook = () => {
    if (onOpenLookbookPanel) {
      onOpenLookbookPanel();
    }
  };

  const selectedVTOResultId = useSelector(selectSelectedVTOResultId);
  const personImagesData = usePersonImages();

  // Local state for delete modal
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  // Progressive rendering for horizontal scroll
  const [visibleCount, setVisibleCount] = useState(12);
  const scrollContainerRef = useRef(null);
  const isLoadingMoreRef = useRef(false);

  // Use RTK Query for VTO results
  const {
    vtoResults: history,
    selectedVTOResult: currentGeneration
  } = useVTOResults();

  // Get all user clothing items for looking up by ID
  const { data: allClothingItems = [] } = useGetUserClothingQuery();

  const [deleteVTOResult, { isLoading: isDeleting }] = useDeleteVTOResultMutation();
  const [updatePersonImage] = useUpdatePersonImageMutationFromPersonImageApi();

  // Horizontal infinite scroll
  const visibleHistory = history ? history.slice(0, visibleCount) : [];
  const hasMore = history && history.length > visibleCount;

  const handleHorizontalScroll = useCallback(() => {
    if (!hasMore || isLoadingMoreRef.current) return;
    const el = scrollContainerRef.current;
    if (!el) return;
    const scrollRight = el.scrollWidth - el.scrollLeft - el.clientWidth;
    if (scrollRight < 150) {
      isLoadingMoreRef.current = true;
      setVisibleCount(prev => prev + 12);
      setTimeout(() => {
        isLoadingMoreRef.current = false;
      }, 300);
    }
  }, [hasMore]);

  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    el.addEventListener('scroll', handleHorizontalScroll, { passive: true });
    return () => el.removeEventListener('scroll', handleHorizontalScroll);
  }, [handleHorizontalScroll]);

  const handleSelectHistory = (result) => {
    // Set active items (clothing worn in this result)
    let clothingItems = [];
    const metadataLayeredItems = result.generationMetadata?.layeredItems || result.metadata?.layeredItems;

    if (metadataLayeredItems && Array.isArray(metadataLayeredItems) && metadataLayeredItems.length > 0) {
      clothingItems = metadataLayeredItems
        .sort((a, b) => (a.order || 0) - (b.order || 0))
        .map(metadataItem => {
          const fullItem = allClothingItems.find(item => item.id === metadataItem.id);
          if (fullItem) {
            // Use the full item from cache
            return {
              ...fullItem,
              imageUrl: fullItem.image?.url
            };
          } else {
            // Fallback
            return {
              id: metadataItem.id,
              name: metadataItem.name || metadataItem.clothesName || 'Unknown Item',
              clothesName: metadataItem.name || metadataItem.clothesName || 'Unknown Item',
              category: metadataItem.category || metadataItem.garmentType,
              image: null
            };
          }
        })
        .filter(item => item != null);
    } else if (result.clothingItems) {
      clothingItems = Array.isArray(result.clothingItems) ? result.clothingItems : [result.clothingItems];
    } else if (result.closetClothes) {
      clothingItems = Array.isArray(result.closetClothes) ? result.closetClothes : [result.closetClothes];
    }
    clothingItems = clothingItems.filter(item => item != null).flat();

    const personId = result.personImage?.id || result.personImageId || result.person_image?.id;
    if (personId) {
      dispatch(setActivePersonImage({ personImageId: personId }));
      dispatch(updateVTOSession({
        personImageId: personId,
        vtoResultId: result.id,
        resultImageId: (Array.isArray(result.resultImage) ? result.resultImage[0]?.url : result.resultImage?.url) || result.imageUrl
      }));

      const fullPersonImageData = personImagesData.personImages?.find(img => img.id === personId);
      const vtoResultUseRemovedFlag = result.useRemovedImage !== false;
      const currentPersonImageUseRemovedFlag = fullPersonImageData?.useRemovedImage !== false;

      if (vtoResultUseRemovedFlag !== currentPersonImageUseRemovedFlag) {
        updatePersonImage({ id: personId, useRemovedImage: vtoResultUseRemovedFlag });
      }
    }

    dispatch(setActiveItems(clothingItems));

    if (result.imageUrl || (Array.isArray(result.resultImage) ? result.resultImage[0]?.url : result.resultImage?.url)) {
      const removedUrl = Array.isArray(result.removedBgImage) ? result.removedBgImage[0]?.url : result.removedBgImage?.url;
      const shouldUseRemoved = result.useRemovedImage !== false && removedUrl;
      const vtoImageUrl = shouldUseRemoved ? removedUrl : (result.imageUrl || (Array.isArray(result.resultImage) ? result.resultImage[0]?.url : result.resultImage?.url));

      const fullPersonImageData = personImagesData.allPersonImages?.find(img => img.id === personId);
      const personImageUseRemovedFlag = fullPersonImageData?.useRemovedImage !== false;
      let originalPersonImageUrl;

      if (personImageUseRemovedFlag && fullPersonImageData?.removedBgImage?.url) {
        originalPersonImageUrl = fullPersonImageData.removedBgImage.url;
      } else if (fullPersonImageData?.image?.url) {
        originalPersonImageUrl = fullPersonImageData.image.url;
      } else {
        originalPersonImageUrl = result.personImageUrl || result.personImage?.url || result.person_image?.url || fullPersonImageData?.url || fullPersonImageData?.image?.url;
      }

      personImageState.setVirtualTryOnResult({
        image: vtoImageUrl,
        originalImage: originalPersonImageUrl,
        metadata: {
          isVirtualTryOnResult: true,
          originalPersonImage: originalPersonImageUrl,
          garmentId: result.closetClothes?.id,
          garmentName: result.closetClothes?.clothesName,
          timestamp: result.created_at || result.createdAt,
          vtoResultId: result.id
        }
      });
      personImageState.setCanvasIntegrationMode(true);
    }

    dispatch(selectVTOResultAction(result.id));
  };

  const handleDeleteClick = (id) => {
    setItemToDelete(id);
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (itemToDelete) {
      try {
        await deleteVTOResult(itemToDelete).unwrap();
        setDeleteConfirmOpen(false);
        setItemToDelete(null);
      } catch (error) {
        console.error('Failed to delete VTO result:', error);
      }
    }
  };

  return (
    <>
      <div className="fit-history-section">
        <div className="section-header-row">
          <div className="section-label" onClick={handleGoToLookbook}>
            LOOKS <Icon name="chevron right" />
          </div>
        </div>
        <div className="history-scroll-container" ref={scrollContainerRef}>
          {history && history.length > 0 ? (
            <div className="history-items">
              {visibleHistory.map((result) => {
                const removedUrl = Array.isArray(result.removedBgImage) ? result.removedBgImage[0]?.url : result.removedBgImage?.url;
                const shouldUseRemoved = result.useRemovedImage !== false && removedUrl;
                const imageUrl = shouldUseRemoved ? removedUrl : ((Array.isArray(result.resultImage) ? result.resultImage[0]?.url : result.resultImage?.url) || result.imageUrl);
                const isActive = selectedVTOResultId === result.id;

                return (
                  <div
                    key={result.id}
                    className={`history-item ${isActive ? 'active' : ''}`}
                    onClick={() => handleSelectHistory(result)}
                  >
                    <div className="history-image-wrapper">
                      <img
                        src={getSmallThumbnailUrl(imageUrl)}
                        alt="VTO result"
                        className="history-image"
                        loading="lazy"
                      />
                      <button
                        className="delete-vto-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteClick(result.originalId || result.id);
                        }}
                        disabled={isDeleting}
                        title="Delete this look"
                      >
                        <Icon name="close" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="empty-history">
              <span>No Look</span>
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <SusuModal
        open={deleteConfirmOpen}
        onClose={() => {
          setDeleteConfirmOpen(false);
          setItemToDelete(null);
        }}
        size="mini"
        className="delete-vto-modal"
        closeIcon
      >
        <SusuModal.Header>
          <Icon name="trash alternate" /> Delete Look
        </SusuModal.Header>
        <SusuModal.Content style={{ position: 'relative' }}>
          <Dimmer active={isDeleting} inverted>
            <Loader size="large">Deleting...</Loader>
          </Dimmer>
          <p>Are you sure you want to delete this look?</p>
        </SusuModal.Content>
        <SusuModal.Actions>
          <Button
            basic
            onClick={() => {
              setDeleteConfirmOpen(false);
              setItemToDelete(null);
            }}
            disabled={isDeleting}
          >
            Cancel
          </Button>
          <Button
            color="red"
            onClick={handleConfirmDelete}
            disabled={isDeleting}
          >
            <Icon name="trash" /> Delete
          </Button>
        </SusuModal.Actions>
      </SusuModal>
    </>
  );
};

export default FitHistorySection;